import { createContext, useContext, useSyncExternalStore, type ReactNode } from 'react'
import type { Project } from '../api/types'
import type { BoardState, BoardStore } from './store'

const StoreContext = createContext<BoardStore | null>(null)

export function StoreProvider({ store, children }: { store: BoardStore; children: ReactNode }) {
  return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>
}

export function useStore(): BoardStore {
  const store = useContext(StoreContext)
  if (!store) throw new Error('useStore needs a StoreProvider')
  return store
}

const NO_SUBSCRIPTION = () => () => {}

/**
 * A part of the board's state for a page that may be drawn without a store
 * (a Markdown text in a test, say): `none` there. `select` must give the same
 * value while the part is unchanged.
 */
export function useBoardPart<T>(select: (state: BoardState) => T, none: T): T {
  const store = useContext(StoreContext)
  return useSyncExternalStore(store ? store.subscribe : NO_SUBSCRIPTION, () => (store ? select(store.getState()) : none))
}

export function useBoardState(): BoardState {
  const store = useStore()
  return useSyncExternalStore(store.subscribe, store.getState)
}

/** The project the pages show: the address's, or the one chosen last; none while the workspace has none. */
export function useSelectedProject(): Project | undefined {
  const { projects, selectedProjectId } = useBoardState()
  return selectedProjectId === null ? undefined : projects.get(selectedProjectId)
}
